import React from "react";
import { Box, HStack, Image, Text, VStack } from "@chakra-ui/react";

import { fixtureStatus, formatKickoff } from "../utils/fixtureStatus";

const TOP_BAR_BLUE = "#00005f";
const CARD_MIN_W = 210;

/** MLB's own crest for a team id. */
function logoUrl(teamId) {
  return teamId ? `https://www.mlbstatic.com/team-logos/${teamId}.svg` : null;
}

function TeamRow({ name, teamId, score, won, showScore }) {
  const src = logoUrl(teamId);
  return (
    <HStack justify="space-between" spacing={2}>
      <HStack spacing={2} minW={0}>
        {src ? (
          <Image src={src} alt="" boxSize="18px" objectFit="contain" flexShrink={0} />
        ) : (
          <Box boxSize="18px" flexShrink={0} />
        )}
        <Text fontSize="xs" noOfLines={1} fontWeight={won ? "bold" : "normal"}>
          {name}
        </Text>
      </HStack>
      {showScore && (
        <Text fontSize="xs" fontWeight={won ? "bold" : "normal"} flexShrink={0}>
          {score}
        </Text>
      )}
    </HStack>
  );
}

/**
 * A sideways-scrolling strip of recent and upcoming games.
 *
 * Each card carries its own date and time, so the strip needs no header and no
 * day grouping. The foot of every card carries the round on the left ("NLDS")
 * and where the series stands on the right ("LAD leads 2-1", "Series tied
 * 1-1", "ATL wins 2-1") — MLB's own wording, so a decided series reads
 * differently from a lead without us having to phrase it.
 *
 * Cards grow to share the width when there are only a few — an off day between
 * rounds leaves one or two, and a row of narrow cards against empty space
 * looks broken rather than quiet. Past the point where they fit, the row
 * scrolls instead.
 *
 * Scores appear on finished games only. A game that has started but is not
 * final shows "In Progress" with no scoreline, because ingest is hourly and a
 * number here would be up to an hour stale. Same bargain the NFL and PL
 * fixture boxes strike, using their `fixtureStatus` so the labels and colours
 * cannot drift apart.
 */
export default function ScoreStrip({ games }) {
  if (!games || games.length === 0) return null;

  return (
    <Box
      bg="white"
      color="black"
      border={`3px solid ${TOP_BAR_BLUE}`}
      borderRadius="lg"
      boxShadow="lg"
      mb={6}
      overflow="hidden"
    >
      <HStack
        spacing={0}
        align="stretch"
        overflowX="auto"
        overflowY="hidden"
        sx={{
          scrollSnapType: "x proximity",
          "::-webkit-scrollbar": { height: "6px" },
          "::-webkit-scrollbar-thumb": { background: "#cbd5e0", borderRadius: "3px" },
        }}
      >
        {games.map((g) => {
          // g.status is MLB's own detailedState, so a game called off reads
          // as such instead of "In Progress" once its start time passes.
          const status = fixtureStatus(g.starts_at, g.is_final, Date.now(), g.status);
          const showScore =
            g.is_final && g.away_score != null && g.home_score != null;
          const awayWon = showScore && g.away_score > g.home_score;
          const homeWon = showScore && g.home_score > g.away_score;

          return (
            <VStack
              key={g.id}
              align="stretch"
              spacing={1}
              // Grows to fill when games are few, scrolls when they are many.
              flex={`1 1 ${CARD_MIN_W}px`}
              minW={`${CARD_MIN_W}px`}
              px={3}
              py={2}
              borderRight="1px solid"
              borderColor="gray.200"
              _last={{ borderRight: "none" }}
              sx={{ scrollSnapAlign: "start" }}
            >
              <HStack justify="space-between" spacing={2}>
                <Text fontSize="10px" color="gray.500" fontWeight="bold" noOfLines={1}>
                  {formatKickoff(g.starts_at)}
                </Text>
                <Text fontSize="10px" fontWeight="bold" color={status.color}
                      whiteSpace="nowrap">
                  {status.label}
                </Text>
              </HStack>

              <TeamRow name={g.away_team} teamId={g.away_team_id}
                       score={g.away_score} won={awayWon} showScore={showScore} />
              <TeamRow name={g.home_team} teamId={g.home_team_id}
                       score={g.home_score} won={homeWon} showScore={showScore} />

              {/* Round on the left, where the series stands on the right. */}
              <HStack justify="space-between" spacing={2} align="baseline">
                <Text fontSize="10px" color="gray.500" noOfLines={1}>
                  {g.series_abbrev || g.series || ""}
                </Text>
                {g.series_status && (
                  <Text
                    fontSize="10px"
                    color="gray.600"
                    fontWeight="semibold"
                    noOfLines={1}
                    flexShrink={0}
                  >
                    {g.series_status}
                  </Text>
                )}
              </HStack>
            </VStack>
          );
        })}
      </HStack>
    </Box>
  );
}
